import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { LanguageSwitch } from '@/components/language-switch';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';

/** Mirrors AcceptInvitationDto — the backend rejects anything shorter. */
const MIN_PASSWORD = 10;

/**
 * Where an invitation email lands (`/invitations/accept?token=…`). Public by
 * necessity: the invitee has no account until this succeeds.
 *
 * The token is the credential, so it is read from the URL and posted straight
 * back — never stored, never logged. On success the backend has set the refresh
 * cookie, so this does a FULL page load rather than a client-side navigate: that
 * is what makes AuthProvider re-run silent auth and pick the session up (same
 * reasoning as the OIDC callback page).
 */
export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) return setError(/* Use at least {{count}} characters. */ t('invite.errorPasswordShort', { count: MIN_PASSWORD }));
    if (password !== confirm) return setError(/* The two passwords do not match. */ t('invite.errorMismatch'));
    setBusy(true);
    try {
      await api.post('/staff/invitations/accept', { token, password });
      window.location.assign('/');
    } catch (err) {
      // Branch on the machine-readable code, not on English (PRD §18.6).
      const code = isApiError(err) ? err.code : 'UNKNOWN';
      setError(
        code === 'INVITE_INVALID'
          ? /* This invitation is no longer valid. It may already have been used or withdrawn — ask fo… */ t('invite.errorInvalid')
          : code === 'INVITE_EXPIRED'
            ? /* This invitation has expired. Ask the person who invited you to send a new one. */ t('invite.errorExpired')
            : code === 'ACCEPT_DISABLED_OIDC'
              ? /* This platform now uses single sign-on. Sign in with your organisation account and your … */ t('invite.errorSsoMode')
              : code === 'KIND_CONFLICT'
                ? /* This email address already belongs to an applicant account. Staff and applicant account… */ t('invite.errorKindConflict')
                : /* We could not activate your account. Please try again, or ask for a new invitation. */ t('invite.errorGeneric'),
      );
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-dvh place-items-center p-6" style={{ background: 'linear-gradient(160deg, #2A3470 0%, #3E4E9D 45%, #5566BB 100%)' }}>
      <div className="w-full max-w-[420px] rounded-[20px] border bg-card px-8 py-9 shadow-[var(--shadow-lg)]">
        <div className="mb-8 flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-xl bg-primary font-display text-lg font-bold text-white">I</span>
          <div className="leading-tight">
            <p className="text-[18px] font-bold text-secondary-foreground">
              {/* AWARDS */ t('app.name')} <span className="font-medium text-muted-foreground">· {/* Ingazi */ t('app.org')}</span>
            </p>
            <p className="text-xs text-muted-foreground">{/* Manage awards and competitions */ t('app.tagline')}</p>
          </div>
        </div>

        <h1 className="mb-2 text-[22px] font-bold text-secondary-foreground">{/* Activate your account */ t('invite.title')}</h1>

        {token === '' ? (
          <>
            <p className="mb-8 text-sm text-muted-foreground">{/* This link is missing its invitation code. Open the link from your invitation email exac… */ t('invite.errorNoToken')}</p>
            <Link to="/login" className="text-sm font-medium text-primary hover:underline">
              {/* Sign in */ t('auth.signIn')}
            </Link>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <p className="mb-6 text-sm text-muted-foreground">{/* You were invited to the AWARDS platform. Choose a password to activate your account and… */ t('invite.blurb')}</p>

            <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="invite-password">
              {/* Password */ t('invite.password')}
            </label>
            <input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mb-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
            <p className="mb-4 text-xs text-muted-foreground">{/* At least {{count}} characters. */ t('invite.passwordHint', { count: MIN_PASSWORD })}</p>

            <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="invite-confirm">
              {/* Confirm password */ t('invite.confirm')}
            </label>
            <input
              id="invite-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mb-6 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
            />

            {error ? (
              <p role="alert" className="mb-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <Button type="submit" size="lg" className="w-full" disabled={busy}>
              {busy ? /* Activating… */ t('invite.activating') : /* Activate account */ t('invite.activate')}
            </Button>
          </form>
        )}

        <div className="mt-8 flex justify-center">
          <LanguageSwitch />
        </div>
      </div>
    </div>
  );
}
